/**
 * Asking the judge: typed questions in, typed answers with probabilities out.
 *
 * A judge is not a fifth role and this is not a fifth pass. It is a model that
 * returns a yes/no, a choice or a score about text it is handed, and it is asked
 * inside a pass that somebody already pressed — a run's source check, a check's
 * reconciliation — never on its own and never on a render (invariant 9). What it
 * says is written down beside the record it describes, stamped with the model
 * that said it, and it decides nothing: D106 draws that line, and every caller
 * in this tree is written against it.
 *
 * The file is the seam. Callers build questions with the three constructors,
 * hand them to `askJudge` with the state they are about, and read the answers
 * back by name. The adapter behind it is `providers/typesafe.ts`; the durability
 * boundary every paid call awaits is awaited here too, so a judge call cannot
 * outrun a save the way no pass can.
 *
 * What may be sent is bounded rather than assumed. The gateway TypeSafe fronts
 * accepts at most thirty-two questions in one request, and a state plus its
 * questions has to fit in roughly sixty-four thousand tokens; a caller that wants
 * more chunks its own work, because a request refused for size after it was
 * built is a refusal this app can make before the network for free.
 */

import { beforePaidCall } from '../core/durability'
import type { ResolvedJudge } from '../core/models'
import type { ModelStamp, Spend } from '../core/types'
import { ModelError } from './providers/contract'
import { runTypeSafe } from './providers/typesafe'

/** Text, or a JSON object or array — what a state, an instruction or a criterion
 *  may be. TypeSafe reads structure, so a state with named parts is preferred to
 *  one paragraph with everything in it. */
export type JudgeJson = null | boolean | number | string | JudgeJson[] | { [k: string]: JudgeJson }

/** A yes/no. The answer is the probability of yes; there is no separate confidence. */
export interface NoulQuestion {
  type: 'noul'
  instructions: JudgeJson
  /** What a yes and a no each mean, when the bare question would be ambiguous. */
  criteria?: { true?: JudgeJson; false?: JudgeJson }
}

/** One of a defined set. The answer is a label, its probability distribution, and
 *  a confidence collapsed from that distribution. */
export interface ChoiceQuestion {
  type: 'choice'
  instructions: JudgeJson
  /** Label → what it means, or null for a label that needs no gloss. */
  criteria: Record<string, JudgeJson>
}

/** A degree along ordered levels, lowest first. The answer is a probability-
 *  weighted level that may fall between two, its distribution, and a confidence. */
export interface ScoreQuestion {
  type: 'score'
  instructions: JudgeJson
  /** At least two, described as concrete situations so each stands on its own. */
  criteria: readonly JudgeJson[]
}

export type JudgeQuestion = NoulQuestion | ChoiceQuestion | ScoreQuestion

export interface NoulAnswer {
  type: 'noul'
  /** P(yes), 0 to 1. Rounded to two places by the service. */
  noul: number
}

export interface ChoiceAnswer {
  type: 'choice'
  /** The highest-probability label — one the question offered, or the call fails. */
  choice: string
  /** Certainty derived from the distribution, 0 to 1. Independent tests found it
   *  no better than the top probability and sometimes worse; read `probabilities`
   *  when a threshold matters. */
  confidence: number
  /** Every offered label → its probability. */
  probabilities: Record<string, number>
}

export interface ScoreAnswer {
  type: 'score'
  /** The expected level, 0-based, possibly between two integers. */
  score: number
  confidence: number
  /** Level index, as a string, → its probability. */
  probabilities: Record<string, number>
}

export type JudgeAnswer = NoulAnswer | ChoiceAnswer | ScoreAnswer

/** The most questions one request may carry. The gateway's bound, adopted here
 *  so a refusal happens before the network rather than after. */
export const JUDGE_QUESTIONS_MAX = 32

export const noul = (
  instructions: JudgeJson,
  criteria?: { true?: JudgeJson; false?: JudgeJson },
): NoulQuestion => ({ type: 'noul', instructions, ...(criteria ? { criteria } : {}) })

export const choice = (
  instructions: JudgeJson,
  criteria: Record<string, JudgeJson>,
): ChoiceQuestion => ({
  type: 'choice',
  instructions,
  criteria,
})

export const score = (instructions: JudgeJson, criteria: readonly JudgeJson[]): ScoreQuestion => ({
  type: 'score',
  instructions,
  criteria,
})

export interface JudgeRequest {
  state: JudgeJson
  questions: Record<string, JudgeQuestion>
}

export interface JudgeResult {
  /** Who answered — the dated version the alias resolved to, off the response. */
  model: ModelStamp
  answers: Record<string, JudgeAnswer>
  /** Billed on input alone, stamped with `model`, for the same ledger every pass
   *  writes to — so the sidebar and the run account carry the judge's draw under
   *  its own name rather than folding it into the pass that asked. */
  spend: Spend
  requestId: string | null
  /** Including the one that succeeded. One when nothing was retried. */
  attempts: number
  startedAt: number
  completedAt: number
}

export interface JudgeOptions {
  signal?: AbortSignal
  timeoutMs?: number
  /** Retries after the first attempt, on rate limits, server errors and dropped
   *  connections only. The evaluation harness passes zero, as it does for every
   *  pass, so an attempt that failed is an attempt it can see. */
  maxRetries?: number
  /** For tests. */
  fetch?: typeof globalThis.fetch
  /** Where to send it. Overrides the endpoint the resolved judge carries, which
   *  on the page is the build's (`JUDGE_ENDPOINT`) and in the harness is absent —
   *  the adapter's default, the API's own host. */
  baseURL?: string
  sleep?: (ms: number) => Promise<void>
}

/**
 * Why a request cannot be sent, or null when it can. Checked before the network
 * and before the durability boundary, because neither should be paid for a
 * request that was never going to be accepted.
 */
export function requestFault(req: JudgeRequest): string | null {
  const names = Object.keys(req.questions)
  if (names.length === 0) return 'a judge request carries at least one question'
  if (names.length > JUDGE_QUESTIONS_MAX) {
    return `a judge request carries at most ${JUDGE_QUESTIONS_MAX} questions; this one carries ${names.length}`
  }
  for (const [name, q] of Object.entries(req.questions)) {
    if (!name.trim()) return 'a question needs a name'
    if (q.type === 'choice' && Object.keys(q.criteria).length < 2) {
      return `choice “${name}” offers fewer than two labels, so there is nothing to choose between`
    }
    if (q.type === 'score' && q.criteria.length < 2) {
      return `score “${name}” has fewer than two levels, so there is nothing to place the answer on`
    }
  }
  return null
}

/**
 * Ask the judge, once, inside a pass that is already paying.
 *
 * Awaits the durability boundary first, exactly as the transport does before
 * every adapter invocation: a judge call is a paid call, and the rule that a
 * memory write is pending work until the durable boundary commits has no
 * exception for small ones.
 */
export async function askJudge(
  j: ResolvedJudge,
  req: JudgeRequest,
  opts: JudgeOptions = {},
): Promise<JudgeResult> {
  const fault = requestFault(req)
  if (fault) throw new ModelError(`The judge was not asked: ${fault}.`, 'request')
  await beforePaidCall()
  const baseURL = opts.baseURL ?? j.baseURL
  return runTypeSafe({
    entry: j.entry,
    apiKey: j.apiKey,
    state: req.state,
    questions: req.questions,
    ...(opts.signal ? { signal: opts.signal } : {}),
    ...(opts.timeoutMs !== undefined ? { timeoutMs: opts.timeoutMs } : {}),
    ...(opts.maxRetries !== undefined ? { maxRetries: opts.maxRetries } : {}),
    ...(opts.fetch ? { fetch: opts.fetch } : {}),
    ...(baseURL ? { baseURL } : {}),
    ...(opts.sleep ? { sleep: opts.sleep } : {}),
  })
}

/** The probability the judge put on `label`, or 0 for a label it was not offered. */
export const probabilityOf = (a: ChoiceAnswer, label: string): number => a.probabilities[label] ?? 0
